import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { LOGO_URL } from "../content";
import { Lock, Mail, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function AdminLogin() {
  const { login, admin, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <FullPageLoader />;
  if (admin) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Check your credentials.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <img src={LOGO_URL} alt="Hari Ram Purwar" className="h-14 w-14 rounded-full ring-2 ring-amber-600/40 object-cover" />
          <div>
            <div className="font-serif text-2xl text-stone-50">Hari Ram Purwar</div>
            <div className="text-amber-500 text-[10px] uppercase tracking-[0.3em]">Partner Console</div>
          </div>
        </div>

        <div className="heritage-card p-8">
          <h1 className="font-serif text-3xl text-stone-50">Welcome back</h1>
          <p className="mt-2 text-stone-400 text-sm">Sign in to view franchise enquiries and reports.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-stone-300 text-xs uppercase tracking-[0.2em]">Email</Label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
                <Input
                  id="email"
                  type="email"
                  data-testid="admin-login-email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 bg-stone-950 border-stone-700 text-stone-100 focus-visible:ring-amber-600 focus-visible:border-amber-600 h-11"
                  placeholder="admin@hariramprwar.com"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-stone-300 text-xs uppercase tracking-[0.2em]">Password</Label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
                <Input
                  id="password"
                  type="password"
                  data-testid="admin-login-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 bg-stone-950 border-stone-700 text-stone-100 focus-visible:ring-amber-600 focus-visible:border-amber-600 h-11"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <div data-testid="admin-login-error" className="text-sm text-red-400 border border-red-900/40 bg-red-950/30 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-stone-950 font-medium uppercase tracking-wider text-sm px-7 py-3 rounded-full btn-primary-glow"
            >
              {submitting ? "Signing in…" : "Sign in"}
              {!submitting && <ArrowRight size={16} />}
            </button>
          </form>
        </div>

        <p className="text-center text-stone-500 text-xs mt-6">
          Tip: bookmark <span className="text-stone-300">/admin</span> for one-click access.
        </p>
      </div>
    </div>
  );
}

function FullPageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}
